/**
 * Job Creation Validation
 * Application-layer validation for job creation workflow
 *
 * Separates validation logic from presentation layer for:
 * - Testability
 * - Reusability
 * - Clear separation of concerns
 */

export interface ValidationError {
  field: string;
  message: string;
}

export interface CustomerData {
  customerId?: string;
  name: string;
  phone: string;
  email?: string;
}

export interface ServiceData {
  serviceType: string;
  containerSize: string;
  materialType: string;
  scheduledDate: string;
  timeWindow?: string;
  priority: string;
  location: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  specialInstructions?: string;
}

export class JobValidation {
  /**
   * Validate customer data
   */
  static validateCustomer(customer: CustomerData | null): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!customer) {
      errors.push({ field: 'customer', message: 'Customer information is required' });
      return errors;
    }

    if (!customer.name || customer.name.trim().length === 0) {
      errors.push({
        field: 'customerSnapshot.name',
        message: 'Customer name is required'
      });
    }

    if (!customer.phone || customer.phone.length < 10) {
      errors.push({
        field: 'customerSnapshot.phone',
        message: 'Phone number must be at least 10 digits'
      });
    }

    if (customer.email && !this.isValidEmail(customer.email)) {
      errors.push({
        field: 'customerSnapshot.email',
        message: 'Email must be a valid email address'
      });
    }

    return errors;
  }

  /**
   * Validate service details
   */
  static validateServiceData(service: ServiceData | null): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!service) {
      errors.push({ field: 'service', message: 'Service details are required' });
      return errors;
    }

    // Material type
    if (!service.materialType || service.materialType.trim().length === 0) {
      errors.push({
        field: 'materialType',
        message: 'Material type is required'
      });
    }

    // Scheduled date
    if (!service.scheduledDate) {
      errors.push({
        field: 'scheduledDate',
        message: 'Scheduled date and time is required'
      });
    } else if (!this.isValidDateFormat(service.scheduledDate)) {
      errors.push({
        field: 'scheduledDate',
        message: 'Please use the date picker to select a valid date'
      });
    } else if (!this.isFutureDate(service.scheduledDate)) {
      errors.push({
        field: 'scheduledDate',
        message: 'Scheduled date must be in the future'
      });
    }

    // Location validation
    errors.push(...this.validateLocation(service.location));

    return errors;
  }

  /**
   * Validate location data
   */
  private static validateLocation(location: ServiceData['location']): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!location.address || location.address.trim().length === 0) {
      errors.push({
        field: 'location.address',
        message: 'Street address is required'
      });
    }

    if (!location.city || location.city.trim().length === 0) {
      errors.push({
        field: 'location.city',
        message: 'City is required'
      });
    }

    if (!location.state || location.state.length !== 2) {
      errors.push({
        field: 'location.state',
        message: 'State must be a 2-letter code (e.g., CA)'
      });
    }

    if (!location.zipCode || location.zipCode.length < 5) {
      errors.push({
        field: 'location.zipCode',
        message: 'Zip code must be at least 5 digits'
      });
    }

    return errors;
  }

  /**
   * Validate complete job creation form
   */
  static validateJobCreation(
    customer: CustomerData | null,
    service: ServiceData | null
  ): ValidationError[] {
    return [
      ...this.validateCustomer(customer),
      ...this.validateServiceData(service),
    ];
  }

  /**
   * Helper: Check if email format is valid
   */
  private static isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Helper: Check if datetime string is in expected format
   * Just ensures basic format - backend will normalize
   */
  private static isValidDateFormat(dateStr: string): boolean {
    // Check for datetime-local format: YYYY-MM-DDTHH:mm
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateStr);
  }

  /**
   * Helper: Check if date is in the future
   */
  private static isFutureDate(dateStr: string): boolean {
    try {
      const date = new Date(dateStr);
      return date > new Date();
    } catch {
      return false;
    }
  }
}
